<script setup lang="ts">
import { computed, inject, onMounted, watch } from 'vue';
import type { ComputedRef } from 'vue';
import { useRouter } from 'vue-router';
import { useUserCourseData } from '../../useUserCourseData';
import ListWrapperCard from 'shared/components/ListWrapperCard/ListWrapperCard.vue';
import SectionDetailsItem from 'modules/Section/components/SectionDetailsItem/SectionDetailsItem.vue';
import TeacherPreviewBanner from 'shared/components/TeacherPreviewBanner/TeacherPreviewBanner.vue';
import { useGlobalStore } from 'src/stores/global';
import { useEvents } from 'src/shared/composables/useEvents/useEvents';
import { useHeaderActions } from 'src/shared/composables/useHeaderActions/useHeaderActions';
import { useQuasar } from 'quasar';
import { t } from 'src/boot/i18n';
import type { UserRole } from 'src/types/User';
import { hasCourseStarted } from '../../utilities/courseProgressState';

const { listen } = useEvents();
const globalStore = useGlobalStore();
const router = useRouter();
const $q = useQuasar();
const currentUserRole =
  inject<ComputedRef<UserRole | undefined>>('currentUserRole');
const isSchoolStudent = computed(() => currentUserRole?.value === 'student');
// Teacher preview: free navigation between sections, and reset is always
// available regardless of the course's is_reset flag.
const isTeacher = computed(() => currentUserRole?.value === 'teacher');

interface Props {
  userCourseDocId: string;
}

const props = defineProps<Props>();

const currentUserId = inject('currentUserId') as ComputedRef<number>;

if (!props.userCourseDocId || !currentUserId)
  throw new Error('userCourseDocId and currentUserId are required');

const {
  courseByCurrentId,
  courseSections,
  fetchCourseSections,
  generateUserSection,
  getProgressSectionData,
  nextCourseSection,
  resetUserCourseProgress,
  userCourseByCurrentId,
  favoriteCourse,
} = useUserCourseData({
  userCourseDocId: props.userCourseDocId,
});

const headerActions = useHeaderActions();

// Watch the course name and update the global store
watch(
  () => userCourseByCurrentId.value?.course?.course_name,
  (newValue) => {
    globalStore.setTitle(newValue || '');
  },
);

watch(
  () => userCourseByCurrentId.value,
  async (userCourse) => {
    if (!userCourse) return;

    if (
      isSchoolStudent.value &&
      userCourse?.is_assigned === false &&
      hasCourseStarted(userCourse)
    ) {
      $q.notify({
        type: 'warning',
        message: t('SCHOOL_UNASSIGNED_PROGRESS_PAUSED_MESSAGE'),
      });
      await router.replace({ name: 'course.my.student' });
    }
  },
  { immediate: true },
);

watch(
  () => [userCourseByCurrentId.value?.course?.is_reset, isTeacher.value],
  ([isReset, teacher]) => {
    if (isReset === true || teacher === true) {
      headerActions.addAction('action:reset');
    } else {
      headerActions.removeAction('action:reset');
    }
  },
  { immediate: true },
);

watch(
  () => userCourseByCurrentId.value?.is_favorite,
  (newValue) => {
    if (newValue === true) {
      headerActions.addAction('action:unfavorite');
      headerActions.removeAction('action:favorite');
    } else {
      headerActions.addAction('action:favorite');
      headerActions.removeAction('action:unfavorite');
    }
  },
  { immediate: true },
);

listen('action:reset', () => {
  if (!props.userCourseDocId || !currentUserId)
    throw new Error('userCourseDocId and currentUserId are required');
  void resetUserCourseProgress();
});

listen('action:favorite', () => {
  void favoriteCourse(props.userCourseDocId, true);
});

listen('action:unfavorite', () => {
  void favoriteCourse(props.userCourseDocId, false);
});

// The gate a STUDENT sees: only the next section (or completed ones) opens.
function isSectionLockedForStudents(
  courseSection: { documentId?: string | null } | null,
) {
  return (
    nextCourseSection?.value?.documentId !== courseSection?.documentId &&
    !getProgressSectionData(courseSection?.documentId ?? undefined)
      ?.completion_date
  );
}

async function handleSectionListItemClick(
  sectionDocId: string | null | undefined,
) {
  if (!sectionDocId) {
    console.error('handleSectionListItemClick: No sectionDocId provided');
    return;
  }
  const userSectionId = await generateUserSection(sectionDocId);
  if (!userSectionId) throw new Error('No User Section to run');

  await router.push({
    name: 'course.sections.section',
    params: {
      userCourseDocId: props.userCourseDocId,
      userSectionId,
      sectionDocId,
    },
  });
}

onMounted(async () => {
  await fetchCourseSections();
  globalStore.setTitle(courseByCurrentId?.value?.course_name || '');
});
</script>
<template>
  <ListWrapperCard
    square
    flat
    class="fit q-pa-none"
  >
    <TeacherPreviewBanner class="q-ma-sm" />
    <q-list class="section-list">
      <SectionDetailsItem
        v-for="courseSection in courseSections"
        :key="courseSection?.id"
        :section-name="courseSection?.section_name ?? ''"
        :subtitle="courseSection?.subtitle"
        :start-date="
          getProgressSectionData(courseSection?.documentId)?.start_date
        "
        :score="getProgressSectionData(courseSection?.documentId)?.score"
        :document-id="courseSection?.documentId ?? ''"
        :time-accumulator="
          getProgressSectionData(courseSection?.documentId)?.time_accumulator
        "
        :active="nextCourseSection?.documentId === courseSection?.documentId"
        :disable="!isTeacher && isSectionLockedForStudents(courseSection)"
        :locked-hint="
          isTeacher && isSectionLockedForStudents(courseSection)
            ? $t('LOCKED_FOR_STUDENTS')
            : null
        "
        @section:selected="handleSectionListItemClick(courseSection?.documentId)"
      />
    </q-list>
  </ListWrapperCard>
</template>

<style scoped>
/*
 * Flex gap instead of q-gutter-y-* + per-row margins: the row margins collapsed
 * out through the wrapping card and pushed the whole list down, leaving a gap
 * above the first row.
 */
.section-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 0;
}
</style>
